import { useState, useMemo, useCallback } from "react";

/**
 * Custom hook for account filtering
 * Follows SRP: Single responsibility for filtering logic
 * Follows OCP: Open for extension (new filters), closed for modification
 */
export const useAccountFilters = (accounts) => {
  const [filters, setFilters] = useState({
    search: "",
    cloud: "",
    status: "",
  });

  /**
   * Apply filters to accounts with improved performance
   */
  const filteredAccounts = useMemo(() => {
    if (!Array.isArray(accounts)) return [];

    return accounts.filter((account) => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const searchableFields = [
          account.AccountName,
          account.AccountId,
          account.AccountOwner,
          account.DomainName,
        ];

        const matchesSearch = searchableFields.some(
          (field) =>
            field && field.toString().toLowerCase().includes(searchTerm)
        );

        if (!matchesSearch) return false;
      }

      // Cloud provider filter
      if (filters.cloud) {
        const provider = account.CloudProvider || "";
        if (
          !provider.toLowerCase().includes(filters.cloud.toLowerCase())
        ) {
          return false;
        }
      }

      // Status filter
      if (filters.status) {
        const isActive = filters.status === "active";
        const accountIsActive = !account.Decommissioned;
        if (accountIsActive !== isActive) {
          return false;
        }
      }

      return true;
    });
  }, [accounts, filters]);

  /**
   * Update search filter
   */
  const setSearchFilter = useCallback((search) => {
    setFilters((prev) => ({ ...prev, search }));
  }, []);

  /**
   * Update cloud provider filter
   */
  const setCloudFilter = useCallback((cloud) => {
    setFilters((prev) => ({ ...prev, cloud }));
  }, []);

  /**
   * Update status filter
   */
  const setStatusFilter = useCallback((status) => {
    setFilters((prev) => ({ ...prev, status }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      search: "",
      cloud: "",
      status: "",
    });
  }, []);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((filter) => filter !== "");
  }, [filters]);

  /**
   * Get filter statistics
   */
  const filterStats = useMemo(() => {
    return {
      total: accounts.length,
      filtered: filteredAccounts.length,
      hasFilters: hasActiveFilters,
    };
  }, [accounts.length, filteredAccounts.length, hasActiveFilters]);

  return {
    filters,
    filteredAccounts,
    setSearchFilter,
    setCloudFilter,
    setStatusFilter,
    clearFilters,
    hasActiveFilters,
    filterStats,
  };
};
